import React, { useEffect, useMemo, useState } from 'react';
import { Box, AddressInput, Button, Icon } from '@wegroup/design-system';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Address } from '../../../../../../../types/Common';
import {
  Enterprise,
  ReturnOptionProps,
} from '../../../../../../../types/ProductFactory';
import { AddressCompany } from '../../../../../../../types/ToolkitAddress';
import { getEnterprisesByAddress } from '../../../../../../common/src/legacy/services/apiRouterService';
import { useDebouncedCallback } from 'use-debounce/lib';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import QuestionGroupSubViewWrapper from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupSubViewWrapper';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import QuestionTitle from '../../../../../../sales-conversation/src/components/legacy/questions/QuestionTitle';
import CompanyRegistrationSearchInput from './CompanyRegistrationSearchInput';
import IllustrationEmptyState from '../../../../../../common/src/assets/images/IllustrationEmptyState';
import StaticMap from '../../../../../../common/src/components/static-map/StaticMap';
import { CompanyRegistrationType } from '../../../../../../parties/src/types';

interface Props {
  hasValue: boolean;
  previousQuestionHandler: () => void;
  nextSubView: () => void;
  handleOnChange: (arg0: Enterprise | boolean) => void;
  onCantFind: () => void;
  countryCode: string;
}

type CompanyOption = {
  value: string;
  label: string;
  registration_number: string;
};

const CompanyRegistrationAddressSearch: React.FC<
  React.PropsWithChildren<Props>
> = ({
  hasValue,
  previousQuestionHandler,
  nextSubView,
  handleOnChange,
  onCantFind,
  countryCode,
}) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState<Address | undefined>();
  const [companies, setCompanies] = useState<AddressCompany[] | undefined>();
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(undefined);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [mode, setMode] = useState<'address' | 'query'>('address');

  const mapCountryToRegistrationType = (
    cc?: string,
  ): CompanyRegistrationType => {
    switch ((cc || '').toUpperCase()) {
      case 'NL':
        return 'KVK';
      case 'CW':
        return 'KVK_CW';
      case 'SX':
        return 'KVK_SX';
      case 'AW':
        return 'KVK_AW';
      // Default to Belgian CBE when unknown or BE
      case 'BE':
      default:
        return 'CBE';
    }
  };

  const companyOptions: CompanyOption[] = useMemo(() => {
    return (
      companies?.map((c) => ({
        value: c.registration_number,
        registration_number: c.registration_number,
        label: `${c.registration_number} - ${c.name}`,
      })) || []
    );
  }, [companies]);

  const validCompanyOptions: CompanyOption[] = useMemo(
    () =>
      companyOptions.filter(
        (opt) =>
          Boolean(
            opt.registration_number &&
              String(opt.registration_number).trim().length > 0,
          ) && Boolean(opt.label && String(opt.label).trim().length > 0),
      ),
    [companyOptions],
  );

  const mapViewPort = useMemo(
    () => ({
      latitude: Number(address?.lat) || 52.1326, // NL center fallback
      longitude: Number(address?.lng) || 5.2913,
      zoom: 17,
    }),
    [address?.lat, address?.lng],
  );

  // Trigger enterprise lookup after pressing "Zoek" in the NL address search
  const handleAddressSearch = (addr?: Address) => {
    setAddress(addr);
    setErrorMessage(undefined);
    setHasSearched(true);
    setMode('address');

    if (!addr || !addr.zipcode || !addr.housenr) {
      setCompanies(undefined);
      setErrorMessage(t('DESIGN-SYSTEM_ADDRESS-INPUT_ADDRESS-NOT-FOUND'));
      return;
    }

    setIsLoadingCompanies(true);
    setSelectedCompanyId(undefined);
    debounceFetchCompaniesByAddress(addr);
  };

  const [debounceFetchCompaniesByAddress] = useDebouncedCallback(
    async (addr: Address) => {
      if (!addr.zipcode || !addr.housenr) return;

      setIsLoadingCompanies(true);
      try {
        const searchParams = {
          zipcode: addr.zipcode,
          housenr: addr.housenr,
          country: (addr.country_code || countryCode || 'BE').toUpperCase(),
        };

        // Prefer backend enterprises search by address if available
        const [resp, status] = await getEnterprisesByAddress(searchParams);

        if (status !== 200) {
          setCompanies([]);
          setErrorMessage(t('Something went wrong'));
          return;
        }

        const items = (resp?.items || []) as Array<{
          name: string;
          cbe?: string;
          company_registration?: { nr?: string };
          address?: Address;
          commercial_name?: string;
        }>;

        const mapped: AddressCompany[] = items
          .map((it) => {
            const registrationNumber =
              it.cbe || it.company_registration?.nr || '';
            return {
              href: '',
              legal_form: '',
              name: it.name || it.commercial_name || '',
              registration_number: registrationNumber,
            } as AddressCompany;
          })
          .filter((m) =>
            Boolean(
              m.registration_number && m.registration_number.trim().length > 0,
            ),
          );

        setCompanies(mapped);
        if ((mapped || []).length === 0) {
          setErrorMessage(
            t('FLOWS_INPUT_COMPANY_REGISTRATION_ADDRESS_NO_RESULTS_USE_LINK'),
          );
        }
      } catch (e) {
        console.error('CompanyRegistrationAddressSearch API call failed:', e);
        setCompanies([]);
        setErrorMessage(t('Something went wrong'));
      } finally {
        setIsLoadingCompanies(false);
      }
    },
    500,
  );

  // Auto-trigger lookup for non-NL addresses when zipcode + housenr are present
  useEffect(() => {
    const cc = (address?.country_code || countryCode || '').toUpperCase();
    if (cc !== 'NL' && address?.zipcode && address?.housenr) {
      setHasSearched(true);
      setIsLoadingCompanies(true);
      setSelectedCompanyId(undefined);
      debounceFetchCompaniesByAddress(address as Address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address?.zipcode, address?.housenr, address?.country_code]);

  const handleSelected = (p: ReturnOptionProps) => {
    const registrationNumber = (p.value as string) || '';
    if (!registrationNumber) {
      return;
    }

    setSelectedCompanyId(registrationNumber);

    const selectedCompany = companies?.find(
      (c) => c.registration_number === registrationNumber,
    );

    // Pass the selected company forward without a second fetch.
    const cc = (address?.country_code || countryCode || 'BE').toUpperCase();
    const enterprise: Enterprise = {
      name: selectedCompany?.name as string,
      cbe: registrationNumber,
      company_registration: {
        nr: registrationNumber,
        type: mapCountryToRegistrationType(cc),
      },
      address: address,
    };

    handleOnChange(enterprise);
  };

  const Question = () => (
    <QuestionGroupAssistantQuestion
      question={t('FLOWS_INPUT_COMPANY_REGISTRATION_SEARCH_QUESTION')}
      description={t('FLOWS_INPUT_COMPANY_REGISTRATION_SEARCH_DESCRIPTION')}
    />
  );

  return (
    <AdvisoryFlowMainContainer>
      <Question />
      <QuestionGroupBody>
        <QuestionGroupSubViewWrapper>
          <Box width="100%" maxW="45rem">
            <Box
              display="flex"
              flexDirection="column"
              gridGap={hasSearched ? '0rem' : '1.5rem'}
            >
              <div>
                {mode === 'address' && !hasSearched && (
                  <>
                    <AddressInput
                      value={address}
                      onChange={(addr?: Address) => setAddress(addr)}
                      onSearch={handleAddressSearch}
                      // Keep the visual search UI only; do not switch to manual mode
                      isManualAllowed={false}
                      // Disable internal address lookup - we handle company search ourselves
                      isLookupAllowed={false}
                      country={address?.country_code || countryCode}
                      isDisabled={isLoadingCompanies}
                    />
                    {/* Toggle positioned after the AddressInput component */}
                    <Box
                      as="button"
                      display="flex"
                      justifyContent="flex-end"
                      mt="1"
                      mb="2"
                      position="relative"
                      zIndex="1"
                      onClick={() => {
                        setMode('query');
                        setCompanies(undefined);
                        setSelectedCompanyId(undefined);
                        setHasSearched(false);
                        setErrorMessage(undefined);
                      }}
                      style={{
                        color: '#6E7485',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                        textDecoration: 'underline',
                        width: '100%',
                      }}
                    >
                      {t('FLOWS_INPUT_SWITCH_TO_QUERY_SEARCH')}
                    </Box>
                  </>
                )}

                {mode === 'query' && (
                  <CompanyRegistrationSearchInput
                    handleOnChange={handleOnChange}
                    onCantFind={onCantFind}
                    countryCode={countryCode}
                  />
                )}
              </div>

              {/* Loading state while searching */}
              {isLoadingCompanies && (
                <LoadingContainer>
                  <IllustrationEmptyState boxSize={48} />
                  <MutedText>{t('LOADING')}</MutedText>
                </LoadingContainer>
              )}

              {!isLoadingCompanies && errorMessage && (
                <>
                  <CenterText>{errorMessage}</CenterText>
                  <Box
                    as="button"
                    display="flex"
                    justifyContent="flex-end"
                    mt="1"
                    mb="2"
                    position="relative"
                    zIndex="1"
                    onClick={() => {
                      setMode('query');
                      setCompanies(undefined);
                      setSelectedCompanyId(undefined);
                      setHasSearched(false);
                      setErrorMessage(undefined);
                    }}
                    style={{
                      color: '#6E7485',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      textDecoration: 'underline',
                      width: '100%',
                    }}
                  >
                    {t('FLOWS_INPUT_SWITCH_TO_QUERY_SEARCH')}
                  </Box>
                </>
              )}

              {/* Address preview with map (when we have company results) */}
              {validCompanyOptions.length > 0 && (
                <PreviewGroup>
                  <PreviewCard>
                    <PreviewHeader>
                      {(address?.street || '') + ' ' + (address?.housenr || '')}
                      {address?.zipcode && address?.city
                        ? `, ${address.zipcode} ${address.city}`
                        : ''}
                    </PreviewHeader>
                    <MapWrapper>
                      <StaticMap
                        showSatelliteAsDefault
                        viewPort={
                          mapViewPort as {
                            latitude: number;
                            longitude: number;
                            zoom: number;
                          }
                        }
                        propertyCoords={[]}
                        data-test-id="Company_Address_StaticMap"
                      />
                    </MapWrapper>
                  </PreviewCard>

                  <UnderCardActions>
                    <BackToSearch
                      onClick={() => {
                        setHasSearched(false);
                        setCompanies(undefined);
                        setSelectedCompanyId(undefined);
                        setAddress(undefined);
                        setErrorMessage(undefined);
                      }}
                    >
                      {t('FLOWS_INPUT_SEARCH_ANOTHER_ADDRESS')}
                    </BackToSearch>
                  </UnderCardActions>
                </PreviewGroup>
              )}

              {/* Results */}
              {!isLoadingCompanies && validCompanyOptions.length > 0 && (
                <Box width="100%">
                  <StyledQuestionTitle>
                    {t('FLOWS_INPUT_COMPANY_REGISTRATION_OR_NAME_LABEL')}:
                  </StyledQuestionTitle>
                  <ListContainer>
                    {validCompanyOptions.map((opt) => (
                      <ListItem
                        key={opt.value}
                        data-selected={
                          selectedCompanyId === opt.value ? 'true' : 'false'
                        }
                        onClick={() =>
                          handleSelected({
                            name: 'company_address_search',
                            label: opt.label,
                            value: opt.value,
                          })
                        }
                      >
                        {opt.label}
                      </ListItem>
                    ))}
                  </ListContainer>
                </Box>
              )}

              {/* Empty state - no fallback needed since user can toggle to query mode */}
              {hasSearched &&
                !isLoadingCompanies &&
                address?.zipcode &&
                address?.housenr &&
                validCompanyOptions.length === 0 && (
                  <EmptyStateContainer>
                    <IllustrationEmptyState boxSize={56} />
                    <CenterText>
                      {t(
                        'FLOWS_INPUT_COMPANY_REGISTRATION_ADDRESS_NO_RESULTS_USE_LINK',
                      )}
                    </CenterText>
                    <Box display="flex" justifyContent="center" mt="1">
                      <Button
                        size="sm"
                        leftIcon={<Icon name="IconSearch" />}
                        onClick={() => {
                          setMode('query');
                          setCompanies(undefined);
                          setSelectedCompanyId(undefined);
                          setHasSearched(false);
                          setErrorMessage(undefined);
                        }}
                      >
                        {t('FLOWS_INPUT_SWITCH_TO_QUERY_SEARCH')}
                      </Button>
                    </Box>
                  </EmptyStateContainer>
                )}
            </Box>
          </Box>
        </QuestionGroupSubViewWrapper>
      </QuestionGroupBody>
      <QuestionGroupActions
        onBack={previousQuestionHandler}
        onContinue={nextSubView}
        disabledContinue={!hasValue}
      />
    </AdvisoryFlowMainContainer>
  );
};

const StyledQuestionTitle = styled(QuestionTitle)`
  width: 100%;
`;

// removed legacy select styles since results are rendered as a simple list

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
`;

const ListContainer = styled.div`
  max-height: 16rem;
  overflow: auto;
  background: white;
  border-radius: 8px;
  border: 1px solid
    ${({ theme }) => (theme.gray && theme.gray[200]) || '#E2E8F0'};
`;

const ListItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid
    ${({ theme }) => (theme.gray && theme.gray[200]) || '#E2E8F0'};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => (theme.gray && theme.gray[50]) || '#F7FAFC'};
  }

  &[data-selected='true'] {
    background: ${({ theme }) =>
      (theme.primary && theme.primary[50]) || '#EBF8FF'};
    outline: 2px solid
      ${({ theme }) => (theme.primary && theme.primary[300]) || '#63B3ED'};
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 0;
  gap: 0.5rem;
`;

const MutedText = styled.p`
  margin-top: 0.75rem;
  color: ${({ theme }) => (theme.gray && theme.gray[500]) || '#6E7485'};
`;

const CenterText = styled.p`
  margin-top: 0.75rem;
  text-align: center;
  color: ${({ theme }) => (theme.gray && theme.gray[600]) || '#4A5568'};
`;

const PreviewCard = styled.div`
  border: 1px solid
    ${({ theme }) => (theme.gray && theme.gray[200]) || '#E2E8F0'};
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const PreviewHeader = styled.div`
  padding: 0.75rem 1rem;
  font-weight: 600;
  border-bottom: 1px solid
    ${({ theme }) => (theme.gray && theme.gray[200]) || '#E2E8F0'};
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 220px;
`;

const BackToSearch = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: ${({ theme }) => (theme.gray && theme.gray[500]) || '#6E7485'};
  text-decoration: none;
  cursor: pointer;
  font-size: 0.875rem;
  &:hover {
    text-decoration: underline;
  }
`;

// Removed PreviewFooter; link moved under the card

const UnderCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 0.25rem;
`;

const PreviewGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export default CompanyRegistrationAddressSearch;
